import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment as env } from '@environments/environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { LOCAL_STORAGE_CONSTANT } from '@shared/constants/local-storage-constant';
import { AuthResponse, AuthUser } from '@shared/models/auth.model';
import { ErrorResponse } from '@shared/models/error.model';
import { AuthService } from '@shared/services/auth.service';
import { HotjarService } from '@shared/services/hotjar.service';
import { ManagedStoreService } from '@shared/services/managed-store.service';
import { CookiesUtil } from '@shared/utils/cookies-util';
import * as dayjs from 'dayjs';
import { jwtDecode as JwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, Subject, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { StoreSyncService } from '../../../services/store-sync.service';
import { ClientIdTypeEnum } from '../../enum/client-id.enum';
import {
  AuthActionTypes,
  LogInAction,
  LogInFailureAction,
  LogInStoreTerminateError,
  LogInSuccessAction,
  LogOutAction,
  RefreshAction
} from '../actions/auth.actions';
import { MenuRequestedAction, ResetMenuAction } from '../actions/menu.action';
import { ClientIdAction, UserInformationAction, UserInformationResetAction } from '../actions/user-info.action';
import { selectMenus } from '../selectors/menu.selector';
import { AppStates } from '../state/app.state';

@Injectable()
export class AuthEffects {
  private readonly cookiesUtil: CookiesUtil;
  private readonly onNavigateToFirstPage$ = new Subject<void>();

  constructor(
    private readonly actions: Actions,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly cookieService: CookieService,
    private readonly hotjarService: HotjarService,
    private readonly logger: NGXLogger,
    private readonly store: Store<AppStates>,
    private readonly storeSyncService: StoreSyncService,
    private managedStoreService: ManagedStoreService
  ) {
    this.cookiesUtil = new CookiesUtil(cookieService);
  }

  LogIn: Observable<Action> = createEffect(() => {
    return this.actions.pipe(
      ofType<LogInAction>(AuthActionTypes.LOGIN),
      map((action) => action.payload),
      switchMap((payload) => {
        return this.authService.logIn(payload).pipe(
          switchMap((resp: AuthResponse) => {
            return of(new UserInformationResetAction(), new LogInSuccessAction(resp));
          }),
          catchError((err) => {
            if (err.error && err.error.message && err.error.message.toLowerCase().includes('no permission')) {
              const updateError = {
                ...err.error,
                code: '01502',
                translateKey: 'ERROR_CODE.01502'
              };
              return of(new LogInFailureAction(updateError));
            }
            return of(new LogInFailureAction(err.error));
          })
        );
      })
    );
  });

  // LogInFailureTestAction: Observable<Action> = createEffect(() => {
  //   return this.actions.pipe(
  //     ofType<LogInFailureTestAction>(AuthActionTypes.LOGIN_FAILURE_TEST),
  //     map((action) => action.payload),
  //     switchMap((payload) => {
  //       if (payload['error_description'] === 'Account is not fully set up') {
  //         return this.authService.bypassAuthenToken().pipe(
  //           switchMap((resp) => {
  //             this.authService
  //               .requiredAction(resp['access_token'])
  //               .subscribe((response) => {
  //                 if (response.data.requiredActions[0] === 'UPDATE_PASSWORD') {
  //                   this.router.navigateByUrl('/reset-password');
  //                 }
  //               });
  //             return of();
  //           }),
  //           catchError((err) => {
  //             return of(new LogInFailureAction(err.error));
  //           })
  //         );
  //       } else if (payload['error_description'] === 'Account disabled') {
  //         const error = {
  //           translateKey:
  //             'Your account has been deactivated. \n' +
  //             'Please contact system admin.',
  //         } as ErrorResponse;
  //         return of(new LogInFailureAction(error));
  //       } else {
  //         const error = {
  //           translateKey: 'Email or Password is not correct.',
  //         } as ErrorResponse;
  //         return of(new LogInFailureAction(error));
  //       }
  //     })
  //   );
  // });

  Refresh: Observable<Action> = createEffect(() => {
    return this.actions.pipe(
      ofType<RefreshAction>(AuthActionTypes.REFRESH),
      map((action) => action.payload),
      switchMap((payload) => {
        return this.authService.refreshToken(payload).pipe(
          switchMap((resp: AuthResponse) => of(new LogInSuccessAction(resp))),
          catchError((err) => of(new LogInFailureAction(err.error)))
        );
      })
    );
  });

  LogInSuccess: Observable<Action> = createEffect(
    () => {
      return this.actions.pipe(
        ofType<LogInSuccessAction>(AuthActionTypes.LOGIN_SUCCESS),
        map((action) => action.payload),
        tap((payload: any) => {
          try {
            const userInfo: AuthUser = JwtDecode(payload.access_token);
            const userName = userInfo.user_name;
            const userAttributes = {
              firstName: userInfo.user_info.firstName,
              lastName: userInfo.user_info.lastName
            };
            this.hotjarService.identifyUser(userName, userAttributes);

            const mSec = payload.expires_in * 1000;
            const currentDate = new Date();
            const expiryDate = new Date(currentDate.getTime() + mSec + env.cookies.bufferTimePeriod);
            const tokenExpiryDate = new Date(dayjs(expiryDate).add(1, 'week').toDate());

            const cookieOptions = {
              expires: expiryDate,
              path: env.cookies.path,
              domain: env.cookies.domain,
              secure: window.location.protocol === 'https:'
            };

            if (payload.access_token) {
              const accessToken = this.cookiesUtil.splitByLength(
                payload.access_token,
                env.cookies.accessToken,
                cookieOptions
              );
              const accessTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.accessToken);

              accessTokenCookiesName.forEach((value, index) => {
                this.cookieService.set(
                  value,
                  accessToken[index],
                  tokenExpiryDate,
                  cookieOptions.path,
                  cookieOptions.domain,
                  cookieOptions.secure
                );
              });
            }

            if (payload.refresh_token) {
              const refreshToken = this.cookiesUtil.splitByLength(
                payload.refresh_token,
                env.cookies.refreshToken,
                cookieOptions
              );
              const refreshTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.refreshToken);

              refreshTokenCookiesName.forEach((value, index) => {
                this.cookieService.set(
                  value,
                  refreshToken[index],
                  tokenExpiryDate,
                  cookieOptions.path,
                  cookieOptions.domain,
                  cookieOptions.secure
                );
              });
            }

            this.dispatchInfo(userInfo);
          } catch (err) {
            this.logger.error(err);
            this.store.dispatch(new LogOutAction());
            window.location.href = window.location.origin;
          }
        }),
        switchMap((payload) => {
          const userInfo: AuthUser = JwtDecode(payload.access_token);
          if (userInfo.user_info.forceChangePassword) {
            return from(this.router.navigate(['/change-password']));
          } else if (this.router.url === '/' || this.router.url.toLocaleLowerCase() === '/') {
            return this.managedStoreService.userManagedStoreList({ pageNumber: 0, pageSize: 2 }).pipe(
              switchMap((response: any) => {
                if (response && response.content.length === 0) {
                  this.store.dispatch(new LogInStoreTerminateError(true));
                  return EMPTY;
                } else if (response && response.content.length === 1) {
                  // pageSize 2: a single item means the user manages exactly one store
                  const storeData = { storeNo: response.content[0].storeNo };

                  localStorage.setItem(LOCAL_STORAGE_CONSTANT.CURRENT_STORE, JSON.stringify(storeData));
                  this.storeSyncService.broadcastStoreChange(storeData as any);
                  return this.authService.getWebVersion().pipe(
                    switchMap((version) => this.navigateToFirstAccessibleRoute(version)),
                    takeUntil(this.onNavigateToFirstPage$)
                  );
                } else {
                  return from(this.router.navigateByUrl('/managed-store'));
                }
              }),
              catchError((err) => {
                return of(err);
              })
            );
          }
          return EMPTY;
        }),
        catchError((err) => {
          this.logger.error(err);
          this.store.dispatch(new LogOutAction());
          window.location.href = window.location.origin;
          return EMPTY;
        })
      );
    },
    { dispatch: false }
  );

  LogInFailure: Observable<Action | ErrorResponse> = createEffect(
    () => {
      return this.actions.pipe(
        ofType<LogInFailureAction>(AuthActionTypes.LOGIN_FAILURE),
        tap(() => {
          this.clearAuth();
          this.router.navigateByUrl('/');
        })
      );
    },
    { dispatch: false }
  );

  LogOut: Observable<Action> = createEffect(
    () => {
      return this.actions.pipe(
        ofType<LogOutAction>(AuthActionTypes.LOGOUT),
        tap(() => {
          window.location.href = '/';
          this.clearAuth();
        })
      );
    },
    { dispatch: false }
  );

  ClearAuth: Observable<Action> = createEffect(
    () => {
      return this.actions.pipe(
        ofType<LogOutAction>(AuthActionTypes.CLEAR_AUTH_REQUEST),
        tap(() => {
          this.clearAuth();
        })
      );
    },
    { dispatch: false }
  );

  private dispatchInfo(userInfo) {
    userInfo.user_info.authorities = userInfo.authorities;
    this.store.dispatch(new UserInformationAction(userInfo.user_info));
    this.store.dispatch(new ClientIdAction(userInfo.client_id));
    this.store.dispatch(new MenuRequestedAction(userInfo.authorities));
  }

  private clearAuth() {
    this.cookieService.deleteAll(env.cookies.path, env.cookies.domain);
    this.logger.info('delete all ', env.cookies.domain, 'cookies ', this.cookieService.getAll());

    this.store.dispatch(new ClientIdAction(env.defaultClientId as ClientIdTypeEnum));
    this.store.dispatch(new UserInformationResetAction());
    this.store.dispatch(new ResetMenuAction());
  }

  private navigateToFirstAccessibleRoute(version): Observable<any> {
    return this.store.select(selectMenus).pipe(
      filter((menus) => !!menus?.length),
      map((menus) => {
        const currentVersion = env.version.split('-')[0];
        const path = menus[0]?.items?.length ? menus[0]?.items[0]?.routerLink[0] : menus[0]?.routerLink[0];
        const updateWebVersion = version && version !== currentVersion;

        if (updateWebVersion) {
          console.log(`Update Web Version!!: from ${currentVersion} to ${version}`);
        }

        return updateWebVersion ? (window.location.href = path) : this.router.navigate([path]);
      }),
      tap(() => this.onNavigateToFirstPage$.next())
    );
  }

  SelectStoreSuccess: Observable<any> = createEffect(
    () => {
      return this.actions.pipe(
        ofType<LogInSuccessAction>(AuthActionTypes.SELECT_STORE_SUCCESS),
        switchMap(() => {
          const storedData = localStorage.getItem(LOCAL_STORAGE_CONSTANT.CURRENT_STORE);
          if (storedData) {
            try {
              const storeData = JSON.parse(storedData);
              this.storeSyncService.broadcastStoreChange(storeData);
            } catch (error) {
              this.logger.error('Error broadcasting store change:', error);
            }
          }
          return this.authService.getWebVersion().pipe(
            switchMap((version) => this.navigateToFirstAccessibleRoute(version)),
            takeUntil(this.onNavigateToFirstPage$)
          );
        })
      );
    },
    { dispatch: false }
  );
}
